<template>
  <f7-popup
    class="feedback-popup"
    :close-by-backdrop-click="false"
    :close-on-escape="false"
    :swipe-to-close="false"
  >
    <f7-view>
      <f7-page class="page-feedback">
        <f7-navbar inner-class="bg-color-blue" class="color-white">
          <f7-nav-title>{{ $t(`page_feedback.title`) }}</f7-nav-title>
        </f7-navbar>

        <div class="page-content-inner">
          <f7-block class="page-feedback-header">
            <div class="page-feedback-rating">
              <span class="page-feedback-rating-info">{{ ratingInfo }}</span>
              <star-rating
                :rating="rating.value"
                :show-rating="rating.showRating"
                :star-size="rating.size"
                :inactive-color="rating.inactiveColor"
                :active-color="rating.activeColor"
                :glow-color="rating.activeColor"
                @rating-selected="val => (rating.value = val)"
              ></star-rating>
            </div>
          </f7-block>

          <span class="page-feedback-title">
            {{ $t(`page_feedback.what_was_wrong`) }}
          </span>

          <f7-block class="no-margin-vertical">
            <fast-feedback :items="negativeComments" @select="toggleSelectFeedback"></fast-feedback>
          </f7-block>

          <span class="page-feedback-title">
            {{ $t(`page_feedback.title`) }}
          </span>

          <f7-list class="page-feedback-textarea no-margin-top" inset>
            <f7-list-input
              type="textarea"
              :placeholder="`${$t(`your`)} ${$t(`comment`)}`"
              :value="comment"
              @input="comment = $event.target.value"
            ></f7-list-input>
          </f7-list>

          <f7-block class="mt-auto">
            <f7-button fill large class="custome-large" @click="send">
              {{ `${$t(`send`)} ${$t(`page_feedback.title`)}` }}
              <f7-preloader v-if="loading" color="white"></f7-preloader>
              <f7-icon v-else f7="arrow_right" color="white" size="24"></f7-icon>
            </f7-button>
          </f7-block>
        </div>
      </f7-page>
    </f7-view>
  </f7-popup>
</template>

<script>
import { mapMutations } from 'vuex';
import apiRequest from '@/js/helpers/apiRequest.js';
import notify from '@/js/helpers/notify.js';

import StarRating from 'vue-star-rating';
import FastFeedback from '@/components/FastFeedback.vue';

export default {
  name: 'Feedback',

  components: {
    StarRating,
    FastFeedback
  },

  props: {
    // eslint-disable-next-line vue/require-default-prop
    lessonId: {
      type: Number
    },

    teacherName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      rating: {
        value: 5,
        size: 30,
        showRating: false,
        inactiveColor: 'rgba(255,255,255, 0.5)',
        activeColor: '#ffd500'
      },
      negativeComments: [],
      comment: '',
      loading: false,
      negativeCommentsLoading: true
    };
  },

  computed: {
    ratingInfo() {
      if (this.rating.value < 3) {
        return this.$t(`bad`);
      } else if (this.rating.value <= 4) {
        return this.$t(`good`);
      }

      return this.$t(`wonderful`);
    }
  },

  created() {
    this.getNegativeComments();
  },

  methods: {
    toggleSelectFeedback(id) {
      const item = this.negativeComments.find(item => item.id === id);
      item.select = !item.select;
    },

    async getNegativeComments() {
      const res = await apiRequest('wrongs', 'GET', null, true);

      this.negativeCommentsLoading = false;

      if (res.error) {
        notify({ text: res.error });
        return;
      }

      this.negativeComments = res.success.map(({ id, name }) => ({
        id,
        name,
        select: false
      }));
    },

    async send() {
      this.loading = true;

      const {
        rating: { value: rating },
        negativeComments,
        comment,
        lessonId
      } = this;

      const wrongComment = negativeComments.filter(item => item.select).map(item => item.name);

      const body = new FormData();
      body.append('lesson_id', lessonId);
      body.append('stars', rating);
      body.append('comment', comment);

      for (let wrong in wrongComment) {
        body.append('wrongs[]', wrong);
      }

      const res = await apiRequest('create/review', 'POST', body, true);

      this.loading = false;

      if (res.error) {
        notify({ text: res.error });
        return;
      }

      notify({
        text: `${this.$t(`notify.thanks_for_feedback`)} 👍`
      });

      this.finishLesson();
      this.deleteAllMessages();

      this.$f7.popup.close('.feedback-popup');

      this.$f7.views.main.router.navigate('/', {
        clearPreviousHistory: true
      });

      this.$f7.views.leftPanel.router.navigate('/main-panel', {
        clearPreviousHistory: true
      });
    },

    ...mapMutations({
      finishLesson: 'lesson/FINISH_LESSON',
      deleteAllMessages: 'chat/DELETE_ALL_MESSAGES'
    })
  }
};
</script>

<style lang="less">
.feedback-popup {
  .page-content {
    --f7-page-navbar-offset: 48px;
  }

  .navbar {
    --f7-navbar-height: 48px;

    .title {
      color: #ffffff;
    }
  }
}

.page-feedback {
  --f7-textarea-height: 80px;
  --f7-textarea-padding-vertical: 10px;
  --f7-list-item-padding-horizontal: 10px;

  @media (min-height: 660px) {
    --f7-textarea-height: 140px;
  }
}

.page-feedback-header {
  margin: 0;
  padding-top: 10px;
  padding-bottom: 10px;
  display: flex;
  flex-direction: column;
  text-align: center;
  color: #ffffff;
  background-color: #32acf5;
  border-bottom-left-radius: 40px;

  @media (min-height: 660px) {
    padding-top: 15px;
    padding-bottom: 15px;
  }

  &-title {
    font-weight: 5;
    font-size: 25px;
    line-height: 30px;
  }

  &-subtitle {
    font-size: 15px;
    color: rgba(#ffffff, 0.7);
    letter-spacing: -0.36px;
    line-height: 20px;
  }
}

.page-feedback-rating {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-height: 660px) {
    margin: 20px;
  }

  &-info {
    margin-bottom: 5px;
    font-size: 12px;
    color: rgba(#ffffff, 0.7);
    line-height: 16px;
  }

  .vue-star-rating-pointer {
    &:not(:last-of-type) {
      margin-right: 5px !important;
    }
  }
}

.page-feedback-title {
  margin-top: 20px;
  margin-bottom: 20px;
  display: block;
  text-align: center;
  font-size: 14px;
  color: #9d9d9d;
  letter-spacing: 0.54px;
  line-height: 16px;
}

.page-feedback-textarea {
  border: 1px solid #d5d8dd;
}
</style>
